/**
 * DOCUMENTATION:
 * Component: CssAnimations
 * Purpose: Educational page detailing CSS Transitions, Transforms, and Keyframe Animations.
 * * * Note for Junior Developers:
 * - A 'Transition' goes from State A to State B (usually triggered by a :hover).
 * - An 'Animation' uses @keyframes and can have infinite states (A -> B -> C -> D).
 * - PERFORMANCE RULE: Only animate 'transform' and 'opacity'. Animating properties like 'width',
 * 'height', or 'margin' forces the browser to recalculate the entire layout on every frame, causing lag.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function CssAnimations() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Animation Properties
    const animationConcepts = useMemo(() => [
        { property: "transition", purpose: "State Changes", description: "Controls the speed of a property change. Shorthand for: property duration timing-function delay (e.g., all 0.3s ease-in)." },
        { property: "transform", purpose: "Hardware Manipulation", description: "Moves, scales, rotates, or skews elements without interrupting the document flow. Very performant." },
        { property: "@keyframes", purpose: "Sequence Definition", description: "Defines the exact stages of an animation using percentages (0% to 100%) or 'from / to'." },
        { property: "animation", purpose: "Applying Keyframes", description: "Shorthand to apply a keyframe. Includes duration, easing, delay, and iteration-count (e.g., infinite)." },
    ], []);

    // Coding examples demonstrating Animations
    const codingModules = useMemo(() => [
        {
            title: "1. The Buttery Smooth Hover (Transitions & Transforms)",
            code: `.card {\n  background-color: #1a0c36;\n  border-radius: 8px;\n  \n  /* Tell the browser to animate ANY changes over 0.3 seconds */\n  transition: transform 0.3s ease, box-shadow 0.3s ease;\n}\n\n.card:hover {\n  /* Scale it up by 5% and lift it visually with a shadow */\n  transform: scale(1.05) translateY(-5px);\n  box-shadow: 0 10px 20px rgba(218, 165, 32, 0.4);\n}`,
            desc: "Always declare the `transition` on the BASE element, not the `:hover` state. If you put it on the hover state, it will animate on the way up, but instantly snap back to normal on the way down."
        },
        {
            title: "2. The Infinite Loading Spinner (@keyframes)",
            code: `/* Step 1: Define the sequence */\n@keyframes spin {\n  0% { transform: rotate(0deg); }\n  100% { transform: rotate(360deg); }\n}\n\n/* Step 2: Apply the sequence */\n.loader {\n  width: 50px;\n  height: 50px;\n  border: 5px solid rgba(255,255,255,0.1);\n  border-top-color: goldenrod;\n  border-radius: 50%;\n  \n  /* animation-name | duration | timing-function | iteration-count */\n  animation: spin 1s linear infinite;\n}`,
            desc: "Keyframes allow an element to run an animation as soon as the page loads, without requiring a user interaction (like a hover) to trigger it. The `linear` timing function ensures the spin doesn't speed up or slow down."
        },
        {
            title: "3. The Staggered Entrance (Animation Delays)",
            code: `@keyframes slideUpFade {\n  from {\n    opacity: 0;\n    transform: translateY(30px);\n  }\n  to {\n    opacity: 1;\n    transform: translateY(0);\n  }\n}\n\n.list-item {\n  opacity: 0; /* Hide initially */\n  animation: slideUpFade 0.5s forwards;\n}\n\n/* Using SCSS logic to stagger the animation delays */\n@for $i from 1 through 5 {\n  .list-item:nth-child(#{$i}) {\n    animation-delay: #{$i * 0.15}s;\n  }\n}`,
            desc: "This is a very common 'premium' UI effect. By applying a slightly longer `animation-delay` to each subsequent child element, they appear to cascade into the screen one after another. The `forwards` keyword ensures the element stays at 100% opacity when the animation finishes."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    ANIMATIONS & TRANSITIONS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Bringing Interfaces to Life
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Motion is essential for modern UI design. It provides user feedback, guides attention, and adds polish to your application. <strong>Transitions</strong> are the simplest form of motion, changing an element from one state to another (like a button hovering). <strong>Keyframe Animations</strong> are more complex, allowing you to define multi-step sequences that can run continuously.
                        </Typography>

                        {/* ✅ Animation Concepts Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Core Motion Properties</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Property</div>
                                    <div>Primary Purpose</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {animationConcepts.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.property}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.purpose}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/css-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
